import React, { useEffect, useState, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeftIcon, SaveIcon, CheckIcon, UserIcon, BuildingIcon, SearchIcon, FileTextIcon, UploadIcon, ClipboardCheckIcon, ArrowRightIcon, ArrowLeftIcon, AlertCircleIcon, CheckCircleIcon, XIcon, LoaderIcon } from 'lucide-react';
interface FormField {
  id: string;
  label: string;
  type: 'text' | 'email' | 'tel' | 'date' | 'select' | 'textarea' | 'file';
  required: boolean;
  options?: string[];
  placeholder?: string;
}
interface FileUploadState {
  uploading: boolean;
  progress: number;
  filename: string;
  error?: string;
}
// Mock profiles for search demonstration
const mockProfiles = {
  individuals: [{
    id: 'ind-001',
    name: 'Abebe Kebede',
    nationalId: 'ETH-1234567',
    email: 'abebe.kebede@example.com',
    phone: '+251 91 234 5678',
    address: 'Addis Ababa, Ethiopia'
  }, {
    id: 'ind-002',
    name: 'Tigist Haile',
    nationalId: 'ETH-7654321',
    email: 'tigist.haile@example.com',
    phone: '+251 92 345 6789',
    address: '456 Churchill Avenue, Addis Ababa'
  }],
  corporations: [{
    id: 'corp-001',
    name: 'Ethio Telecom',
    registrationNumber: 'BRN-9876543',
    taxId: 'TIN-123456789',
    email: 'info@ethiotelecom.et',
    phone: '+251 11 463 1212',
    address: 'Churchill Avenue, Addis Ababa'
  }, {
    id: 'corp-002',
    name: 'Commercial Bank of Ethiopia',
    registrationNumber: 'BRN-1234567',
    taxId: 'TIN-987654321',
    email: 'info@cbe.com.et',
    phone: '+251 11 551 5000',
    address: 'Ras Mekonnen Avenue, Addis Ababa'
  }]
};
export const ServiceRequestFormPage = () => {
  const {
    serviceId,
    citizenId
  } = useParams();
  const navigate = useNavigate();
  // Form state
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [fileUploads, setFileUploads] = useState<Record<string, FileUploadState>>({});
  const [loading, setLoading] = useState(false);
  // Service and user information
  const [serviceName, setServiceName] = useState('');
  const [citizenName, setCitizenName] = useState('');
  // Multi-step form state
  const [currentStep, setCurrentStep] = useState(1);
  const [requesterType, setRequesterType] = useState<'individual' | 'corporation' | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<any>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [showNewProfileForm, setShowNewProfileForm] = useState(false);
  // Form fields - these would be fetched based on the service type in a real app
  const formFields: FormField[] = [{
    id: 'fullName',
    label: 'Full Name',
    type: 'text',
    required: true,
    placeholder: 'Enter full name as per ID'
  }, {
    id: 'email',
    label: 'Email Address',
    type: 'email',
    required: true,
    placeholder: 'Enter email address'
  }, {
    id: 'phone',
    label: 'Phone Number',
    type: 'tel',
    required: true,
    placeholder: 'Enter phone number'
  }, {
    id: 'address',
    label: 'Physical Address',
    type: 'text',
    required: true,
    placeholder: 'Enter your current address'
  }, {
    id: 'requestDate',
    label: 'Requested Date',
    type: 'date',
    required: true
  }, {
    id: 'documentType',
    label: 'Document Type',
    type: 'select',
    required: true,
    options: ['Birth Certificate', 'Marriage Certificate', 'Death Certificate', 'Property Deed', 'Business License']
  }, {
    id: 'additionalInfo',
    label: 'Additional Information',
    type: 'textarea',
    required: false,
    placeholder: 'Enter any additional details about your request'
  }, {
    id: 'supportingDocs',
    label: 'Supporting Documents',
    type: 'file',
    required: false
  }];
  useEffect(() => {
    // In a real app, you would fetch service details based on serviceId
    // and citizen details based on citizenId
    setServiceName('Document Request');
    setCitizenName('Abebe Kebede');
    // Pre-fill form with citizen data if available
    if (citizenId) {
      setFormData({
        fullName: 'Abebe Kebede',
        email: 'abebe.kebede@example.com',
        phone: '+251 91 234 5678',
        address: 'Addis Ababa, Ethiopia'
      });
      setRequesterType('individual');
      setSelectedProfile(mockProfiles.individuals[0]);
      setCurrentStep(3); // Skip to service-specific info
    }
  }, [serviceId, citizenId]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, fieldId: string) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      setFileUploads(prev => ({
        ...prev,
        [fieldId]: {
          uploading: false,
          progress: 0,
          filename: file.name,
          error: 'File size exceeds 10MB limit'
        }
      }));
      return;
    }
    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setFileUploads(prev => ({
        ...prev,
        [fieldId]: {
          uploading: false,
          progress: 0,
          filename: file.name,
          error: 'Invalid file type. Only PDF, JPG, and PNG files are allowed.'
        }
      }));
      return;
    }
    // Start upload process
    setFileUploads(prev => ({
      ...prev,
      [fieldId]: {
        uploading: true,
        progress: 0,
        filename: file.name
      }
    }));
    // Simulate file upload with progress
    const uploadFile = async () => {
      try {
        // Simulate upload progress
        for (let progress = 0; progress <= 100; progress += 10) {
          await new Promise(resolve => setTimeout(resolve, 100));
          setFileUploads(prev => ({
            ...prev,
            [fieldId]: {
              ...prev[fieldId],
              progress
            }
          }));
        }
        // Simulate successful upload response
        const mockFileId = `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const fileSize = (file.size / 1024 / 1024).toFixed(2) + ' MB';
        setFormData(prev => ({
          ...prev,
          [`${fieldId}_file_id`]: mockFileId,
          [`${fieldId}_filename`]: file.name,
          [`${fieldId}_filesize`]: fileSize,
          [`${fieldId}_file_type`]: file.type
        }));
        // Clear upload state
        setFileUploads(prev => {
          const newState = {
            ...prev
          };
          delete newState[fieldId];
          return newState;
        });
      } catch (error) {
        setFileUploads(prev => ({
          ...prev,
          [fieldId]: {
            uploading: false,
            progress: 0,
            filename: file.name,
            error: 'Upload failed. Please try again.'
          }
        }));
      }
    };
    uploadFile();
  };
  const handleRemoveFile = (fieldId: string) => {
    setFormData(prev => {
      const newData = {
        ...prev
      };
      delete newData[`${fieldId}_file_id`];
      delete newData[`${fieldId}_filename`];
      delete newData[`${fieldId}_filesize`];
      delete newData[`${fieldId}_file_type`];
      return newData;
    });
  };
  const handleSaveDraft = async () => {
    setLoading(true);
    try {
      // Simulate API call to save draft
      await new Promise(resolve => setTimeout(resolve, 1000));
      // Show success feedback (you might want to add a toast notification here)
      console.log('Draft saved successfully');
    } catch (error) {
      console.error('Failed to save draft:', error);
    } finally {
      setLoading(false);
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setLoading(false);
      // Navigate to success page or back to service page
      navigate('/service-request-success', {
        state: {
          serviceName,
          referenceNumber: `REQ-${Date.now().toString().slice(-6)}`
        }
      });
    }, 1500);
  };
  const handleSearch = () => {
    if (!searchQuery.trim()) return;
    setIsSearching(true);
    setSearchPerformed(false);
    // Simulate API search
    setTimeout(() => {
      let results = [];
      if (requesterType === 'individual') {
        results = mockProfiles.individuals.filter(profile => profile.name.toLowerCase().includes(searchQuery.toLowerCase()) || profile.nationalId.toLowerCase().includes(searchQuery.toLowerCase()) || profile.email.toLowerCase().includes(searchQuery.toLowerCase()));
      } else {
        results = mockProfiles.corporations.filter(profile => profile.name.toLowerCase().includes(searchQuery.toLowerCase()) || profile.registrationNumber.toLowerCase().includes(searchQuery.toLowerCase()) || profile.taxId.toLowerCase().includes(searchQuery.toLowerCase()));
      }
      setSearchResults(results);
      setSearchPerformed(true);
      setIsSearching(false);
    }, 1000);
  };
  const handleSelectProfile = (profile: any) => {
    setSelectedProfile(profile);
    // Pre-fill form data based on profile
    if (requesterType === 'individual') {
      setFormData({
        fullName: profile.name,
        email: profile.email,
        phone: profile.phone,
        address: profile.address
      });
    } else {
      setFormData({
        companyName: profile.name,
        companyEmail: profile.email,
        companyPhone: profile.phone,
        companyAddress: profile.address,
        registrationNumber: profile.registrationNumber,
        taxId: profile.taxId
      });
    }
  };
  const handleNextStep = () => {
    // Validate current step
    if (currentStep === 1 && !requesterType) {
      return; // Require requester type selection
    }
    if (currentStep === 2 && !selectedProfile && !showNewProfileForm) {
      return; // Require profile selection or new profile form
    }
    // Move to next step
    setCurrentStep(prev => prev + 1);
    // Scroll to top
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const handlePrevStep = () => {
    setCurrentStep(prev => Math.max(1, prev - 1));
    // Scroll to top
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const renderStepIndicator = () => {
    const steps = [{
      number: 1,
      label: 'Requester Type'
    }, {
      number: 2,
      label: 'Identification'
    }, {
      number: 3,
      label: 'Service Details'
    }, {
      number: 4,
      label: 'Documents'
    }, {
      number: 5,
      label: 'Review & Submit'
    }];
    return <div className="mb-8" data-id="element-4882">
        <div className="hidden md:flex items-center justify-between" data-id="element-4883">
          {steps.map((step, index) => <Fragment key={step.number} data-id="element-4884">
              <div className="flex flex-col items-center" data-id="element-4885">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full ${currentStep === step.number ? 'bg-blue-600 text-white' : currentStep > step.number ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-600'} transition-colors duration-200`} data-id="element-4886">
                  {currentStep > step.number ? <CheckIcon className="w-5 h-5" data-id="element-4887" /> : <span data-id="element-4888">{step.number}</span>}
                </div>
                <div className="text-xs mt-2 font-medium text-gray-600" data-id="element-4889">
                  {step.label}
                </div>
              </div>
              {index < steps.length - 1 && <div className={`w-full h-1 ${currentStep > index + 1 ? 'bg-green-600' : 'bg-gray-200'} transition-colors duration-200`} data-id="element-4890" />}
            </Fragment>)}
        </div>
        {/* Mobile step indicator */}
        <div className="md:hidden" data-id="element-4891">
          <div className="flex items-center justify-between mb-4" data-id="element-4892">
            <div className="text-sm font-medium text-gray-600" data-id="element-4893">
              Step {currentStep} of {steps.length}
            </div>
            <div className="text-sm font-medium text-gray-900" data-id="element-4894">
              {steps[currentStep - 1].label}
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-4895">
            <div className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" style={{
            width: `${currentStep / steps.length * 100}%`
          }} data-id="element-4896"></div>
          </div>
        </div>
      </div>;
  };
  const renderRequesterTypeStep = () => {
    return <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-4897">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200" data-id="element-4898">
          <h2 className="text-lg font-medium text-gray-900" data-id="element-4899">
            Select Requester Type
          </h2>
          <p className="text-sm text-gray-500" data-id="element-4900">
            Please select the type of entity making this service request
          </p>
        </div>
        <div className="p-6" data-id="element-4901">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4902">
            <button type="button" onClick={() => setRequesterType('individual')} className={`flex flex-col items-center p-6 border-2 rounded-lg ${requesterType === 'individual' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'} transition-colors duration-200`} data-id="element-4903">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center ${requesterType === 'individual' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`} data-id="element-4904">
                <UserIcon className="w-8 h-8" data-id="element-4905" />
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900" data-id="element-4906">
                Individual
              </h3>
              <p className="mt-2 text-sm text-gray-500 text-center" data-id="element-4907">
                Apply as a citizen or resident for personal services
              </p>
            </button>
            <button type="button" onClick={() => setRequesterType('corporation')} className={`flex flex-col items-center p-6 border-2 rounded-lg ${requesterType === 'corporation' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'} transition-colors duration-200`} data-id="element-4908">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center ${requesterType === 'corporation' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`} data-id="element-4909">
                <BuildingIcon className="w-8 h-8" data-id="element-4910" />
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900" data-id="element-4911">
                Corporation
              </h3>
              <p className="mt-2 text-sm text-gray-500 text-center" data-id="element-4912">
                Apply on behalf of a business or organization
              </p>
            </button>
          </div>
          {/* Privacy notice */}
          <div className="mt-8 bg-gray-50 p-4 rounded-lg border border-gray-200" data-id="element-4913">
            <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-4914">
              Privacy Notice
            </h4>
            <p className="text-xs text-gray-600" data-id="element-4915">
              Your information will be processed in accordance with our privacy
              policy. We collect only the information necessary to process your
              service request.
              <a href="#" className="text-blue-600 hover:text-blue-800 ml-1" data-id="element-4916">
                Read our full privacy policy
              </a>
            </p>
          </div>
        </div>
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center" data-id="element-4917">
          <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors" data-id="element-4918">
            <ArrowLeftIcon className="mr-2 h-4 w-4" data-id="element-4919" />
            Cancel
          </button>
          <div className="flex space-x-3" data-id="element-4920">
            <button type="button" onClick={handleSaveDraft} disabled={loading} className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded-md shadow-sm hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-75 disabled:cursor-not-allowed transition-colors" data-id="element-4921">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-4922" />
              Save Draft
            </button>
            <button type="button" onClick={handleNextStep} className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors" data-id="element-4923">
              Continue
              <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-4924" />
            </button>
          </div>
        </div>
      </div>;
  };
  const renderSearchAndVerificationStep = () => {
    return <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-4925">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200" data-id="element-4926">
          <h2 className="text-lg font-medium text-gray-900" data-id="element-4927">
            {requesterType === 'individual' ? 'Find Your Profile' : 'Find Company Profile'}
          </h2>
          <p className="text-sm text-gray-500" data-id="element-4928">
            {requesterType === 'individual' ? 'Search for your existing profile using your ID number, name, or email' : 'Search for your company using registration number, tax ID, or company name'}
          </p>
        </div>
        <div className="p-6" data-id="element-4929">
          {/* Search Form */}
          <div className="mb-6" data-id="element-4930">
            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3" data-id="element-4931">
              <div className="flex-1 relative" data-id="element-4932">
                <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder={requesterType === 'individual' ? 'Enter ID number, name, or email' : 'Enter registration number, tax ID, or company name'} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-4933" />
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-4934" />
              </div>
              <button type="button" onClick={handleSearch} disabled={isSearching || !searchQuery.trim()} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center" data-id="element-4935">
                {isSearching ? <>
                    <LoaderIcon className="animate-spin mr-2 h-4 w-4" data-id="element-4936" />
                    Searching...
                  </> : <>
                    <SearchIcon className="mr-2 h-4 w-4" data-id="element-4937" />
                    Search
                  </>}
              </button>
            </div>
          </div>
          {/* Search Results */}
          {searchPerformed && <div className="mb-6 border border-gray-200 rounded-lg overflow-hidden" data-id="element-4938">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200" data-id="element-4939">
                <h3 className="text-sm font-medium text-gray-900" data-id="element-4940">
                  Search Results ({searchResults.length})
                </h3>
              </div>
              {searchResults.length > 0 ? <div className="divide-y divide-gray-200" data-id="element-4941">
                  {searchResults.map(profile => <div key={profile.id} className={`p-4 hover:bg-gray-50 cursor-pointer ${selectedProfile?.id === profile.id ? 'bg-blue-50' : ''}`} onClick={() => handleSelectProfile(profile)} data-id="element-4942">
                      <div className="flex items-center justify-between" data-id="element-4943">
                        <div className="flex items-center" data-id="element-4944">
                          <div className="flex-shrink-0" data-id="element-4945">
                            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center" data-id="element-4946">
                              {requesterType === 'individual' ? <UserIcon className="h-5 w-5 text-gray-500" data-id="element-4947" /> : <BuildingIcon className="h-5 w-5 text-gray-500" data-id="element-4948" />}
                            </div>
                          </div>
                          <div className="ml-4" data-id="element-4949">
                            <h4 className="text-sm font-medium text-gray-900" data-id="element-4950">
                              {profile.name}
                            </h4>
                            <p className="text-xs text-gray-500" data-id="element-4951">
                              {requesterType === 'individual' ? `ID: ${profile.nationalId}` : `Reg: ${profile.registrationNumber}`}
                            </p>
                          </div>
                        </div>
                        {selectedProfile?.id === profile.id && <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-4952" />}
                      </div>
                    </div>)}
                </div> : <div className="p-6 text-center" data-id="element-4953">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100" data-id="element-4954">
                    <AlertCircleIcon className="h-6 w-6 text-gray-400" data-id="element-4955" />
                  </div>
                  <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-4956">
                    No results found
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-4957">
                    We couldn't find any matching{' '}
                    {requesterType === 'individual' ? 'profiles' : 'companies'}.
                  </p>
                  <div className="mt-6" data-id="element-4958">
                    <button type="button" onClick={() => setShowNewProfileForm(true)} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700" data-id="element-4959">
                      {requesterType === 'individual' ? 'Register New Profile' : 'Register New Company'}
                    </button>
                  </div>
                </div>}
            </div>}
          {/* Selected Profile Summary */}
          {selectedProfile && <div className="mb-6 border border-green-200 rounded-lg bg-green-50 p-4" data-id="element-4960">
              <div className="flex items-start" data-id="element-4961">
                <div className="flex-shrink-0" data-id="element-4962">
                  <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-4963" />
                </div>
                <div className="ml-3" data-id="element-4964">
                  <h3 className="text-sm font-medium text-green-800" data-id="element-4965">
                    Profile Selected
                  </h3>
                  <div className="mt-2 text-sm text-green-700" data-id="element-4966">
                    <p className="font-medium" data-id="element-4967">{selectedProfile.name}</p>
                    <p data-id="element-4968">
                      {requesterType === 'individual' ? `ID: ${selectedProfile.nationalId}` : `Registration: ${selectedProfile.registrationNumber}`}
                    </p>
                    <p data-id="element-4969">{selectedProfile.email}</p>
                    <p data-id="element-4970">{selectedProfile.phone}</p>
                  </div>
                </div>
                <div className="ml-auto" data-id="element-4971">
                  <button type="button" onClick={() => setSelectedProfile(null)} className="inline-flex items-center p-1.5 border border-green-300 rounded-full text-green-600 hover:bg-green-100" data-id="element-4972">
                    <XIcon className="h-4 w-4" data-id="element-4973" />
                  </button>
                </div>
              </div>
            </div>}
          {/* New Profile Form (simplified for demo) */}
          {showNewProfileForm && <div className="mb-6 border border-gray-200 rounded-lg p-4" data-id="element-4974">
              <div className="flex justify-between items-center mb-4" data-id="element-4975">
                <h3 className="text-sm font-medium text-gray-900" data-id="element-4976">
                  {requesterType === 'individual' ? 'Register New Profile' : 'Register New Company'}
                </h3>
                <button type="button" onClick={() => setShowNewProfileForm(false)} className="inline-flex items-center p-1.5 border border-gray-300 rounded-full text-gray-500 hover:bg-gray-100" data-id="element-4977">
                  <XIcon className="h-4 w-4" data-id="element-4978" />
                </button>
              </div>
              <div className="grid grid-cols-1 gap-4" data-id="element-4979">
                {requesterType === 'individual' ? <>
                    <div data-id="element-4980">
                      <label htmlFor="fullName" className="block text-sm font-medium text-gray-700" data-id="element-4981">
                        Full Name <span className="text-red-500" data-id="element-4982">*</span>
                      </label>
                      <input type="text" id="fullName" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Enter your full name" data-id="element-4983" />
                    </div>
                    <div data-id="element-4984">
                      <label htmlFor="nationalId" className="block text-sm font-medium text-gray-700" data-id="element-4985">
                        National ID <span className="text-red-500" data-id="element-4986">*</span>
                      </label>
                      <input type="text" id="nationalId" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Enter your national ID number" data-id="element-4987" />
                    </div>
                  </> : <>
                    <div data-id="element-4988">
                      <label htmlFor="companyName" className="block text-sm font-medium text-gray-700" data-id="element-4989">
                        Company Name <span className="text-red-500" data-id="element-4990">*</span>
                      </label>
                      <input type="text" id="companyName" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Enter company name" data-id="element-4991" />
                    </div>
                    <div data-id="element-4992">
                      <label htmlFor="registrationNumber" className="block text-sm font-medium text-gray-700" data-id="element-4993">
                        Business Registration Number{' '}
                        <span className="text-red-500" data-id="element-4994">*</span>
                      </label>
                      <input type="text" id="registrationNumber" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Enter business registration number" data-id="element-4995" />
                    </div>
                  </>}
                <div data-id="element-4996">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700" data-id="element-4997">
                    Email Address <span className="text-red-500" data-id="element-4998">*</span>
                  </label>
                  <input type="email" id="email" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Enter email address" data-id="element-4999" />
                </div>
                <div data-id="element-5000">
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700" data-id="element-5001">
                    Phone Number <span className="text-red-500" data-id="element-5002">*</span>
                  </label>
                  <input type="tel" id="phone" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Enter phone number" data-id="element-5003" />
                </div>
                <div className="mt-4" data-id="element-5004">
                  <button type="button" className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700" onClick={() => {
                // Simulate creating a new profile
                const newProfile = {
                  id: `new-${Date.now()}`,
                  name: requesterType === 'individual' ? 'New User' : 'New Company',
                  nationalId: 'NEW-12345',
                  registrationNumber: 'BRN-NEW123',
                  email: 'new@example.com',
                  phone: '+251 91 000 0000',
                  address: 'Addis Ababa, Ethiopia'
                };
                setSelectedProfile(newProfile);
                setShowNewProfileForm(false);
              }} data-id="element-5005">
                    Register and Continue
                  </button>
                </div>
              </div>
            </div>}
          {/* No profile yet message */}
          {!searchPerformed && !selectedProfile && !showNewProfileForm && <div className="bg-gray-50 rounded-lg p-6 text-center" data-id="element-5006">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100" data-id="element-5007">
                <SearchIcon className="h-6 w-6 text-blue-600" data-id="element-5008" />
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-5009">
                Search for your profile
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-5010">
                Enter your details in the search box above to find your existing
                profile.
              </p>
              <div className="mt-6" data-id="element-5011">
                {/* The following code is replaced as per instruction */}
                <button type="button"
            // Navigate to a new registration page instead of showing a modal
            onClick={() => {
              if (requesterType === 'individual') {
                navigate(`/assisted-creation/register-profile/${serviceId}`);
              } else {
                navigate(`/assisted-creation/register-company/${serviceId}`);
              }
            }} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200" data-id="element-5012">
                  {requesterType === 'individual' ? 'Register New Profile' : 'Register New Company'}
                </button>
              </div>
            </div>}
        </div>
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center" data-id="element-5013">
          <button type="button" onClick={handlePrevStep} className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors" data-id="element-5014">
            <ArrowLeftIcon className="mr-2 h-4 w-4" data-id="element-5015" />
            Back
          </button>
          <div className="flex space-x-3" data-id="element-5016">
            <button type="button" onClick={handleSaveDraft} disabled={loading} className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded-md shadow-sm hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-75 disabled:cursor-not-allowed transition-colors" data-id="element-5017">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-5018" />
              Save Draft
            </button>
            <button type="button" onClick={handleNextStep} className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors" data-id="element-5019">
              Continue
              <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-5020" />
            </button>
          </div>
        </div>
      </div>;
  };
  const renderServiceDetailsStep = () => {
    return <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-5021">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200" data-id="element-5022">
          <h2 className="text-lg font-medium text-gray-900" data-id="element-5023">Service Details</h2>
          <p className="text-sm text-gray-500" data-id="element-5024">
            Please provide the necessary information for your {serviceName}{' '}
            request
          </p>
        </div>
        <div className="p-6" data-id="element-5025">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5026">
            {formFields.slice(0, 6).map(field => <div key={field.id} className={field.type === 'textarea' ? 'md:col-span-2' : ''} data-id="element-5027">
                <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5028">
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1" data-id="element-5029">*</span>}
                </label>
                <div data-id="element-5030">
                  {field.type === 'textarea' ? <textarea id={field.id} name={field.id} rows={4} className="block w-full px-3 py-2 rounded-md border border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-colors" placeholder={field.placeholder} value={formData[field.id] || ''} onChange={handleInputChange} required={field.required} data-id="element-5031" /> : field.type === 'select' ? <div className="relative" data-id="element-5032">
                      <select id={field.id} name={field.id} className="block w-full appearance-none px-3 py-2 rounded-md border border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-colors bg-white" value={formData[field.id] || ''} onChange={handleInputChange} required={field.required} data-id="element-5033">
                        <option value="" data-id="element-5034">Select an option</option>
                        {field.options?.map(option => <option key={option} value={option} data-id="element-5035">
                            {option}
                          </option>)}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500" data-id="element-5036">
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-5037">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" data-id="element-5038" />
                        </svg>
                      </div>
                    </div> : <input id={field.id} name={field.id} type={field.type} className="block w-full px-3 py-2 rounded-md border border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-colors" placeholder={field.placeholder} value={formData[field.id] || ''} onChange={handleInputChange} required={field.required} data-id="element-5039" />}
                </div>
              </div>)}
          </div>
          {/* Additional Information */}
          <div className="mt-8" data-id="element-5040">
            <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5041">
              Additional Information
            </label>
            <div data-id="element-5042">
              <textarea id="additionalInfo" name="additionalInfo" rows={4} className="block w-full px-3 py-2 rounded-md border border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-colors" placeholder="Enter any additional details about your request" value={formData.additionalInfo || ''} onChange={handleInputChange} data-id="element-5043" />
            </div>
          </div>
        </div>
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center" data-id="element-5044">
          <button type="button" onClick={handlePrevStep} className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors" data-id="element-5045">
            <ArrowLeftIcon className="mr-2 h-4 w-4" data-id="element-5046" />
            Back
          </button>
          <div className="flex space-x-3" data-id="element-5047">
            <button type="button" onClick={handleSaveDraft} disabled={loading} className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded-md shadow-sm hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-75 disabled:cursor-not-allowed transition-colors" data-id="element-5048">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-5049" />
              Save Draft
            </button>
            <button type="button" onClick={handleNextStep} className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors" data-id="element-5050">
              Continue
              <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-5051" />
            </button>
          </div>
        </div>
      </div>;
  };
  const renderDocumentUploadStep = () => {
    return <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-5052">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200" data-id="element-5053">
          <h2 className="text-lg font-medium text-gray-900" data-id="element-5054">
            Supporting Documents
          </h2>
          <p className="text-sm text-gray-500" data-id="element-5055">
            Please upload all required documents for your request
          </p>
        </div>
        <div className="p-6" data-id="element-5056">
          <div className="space-y-6" data-id="element-5057">
            {formFields.filter(field => field.type === 'file').map(field => <div key={field.id} className="mb-6" data-id="element-5058">
                  <div className="flex justify-between items-center mb-2" data-id="element-5059">
                    <label htmlFor={field.id} className="block text-sm font-medium text-gray-700" data-id="element-5060">
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1" data-id="element-5061">*</span>}
                    </label>
                    {formData[`${field.id}_file_id`] && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-5062">
                        <svg className="mr-1.5 h-2 w-2 text-green-400" fill="currentColor" viewBox="0 0 8 8" data-id="element-5063">
                          <circle cx="4" cy="4" r="3" data-id="element-5064" />
                        </svg>
                        Uploaded
                      </span>}
                  </div>
                  <div className="mt-1 border border-gray-300 rounded-lg overflow-hidden" data-id="element-5065">
                    {formData[`${field.id}_file_id`] ? <div className="flex items-start p-4 bg-gray-50" data-id="element-5066">
                        <div className="flex-shrink-0" data-id="element-5067">
                          {formData[`${field.id}_file_type`]?.startsWith('image/') ? <div className="h-16 w-16 rounded bg-gray-100 flex items-center justify-center overflow-hidden" data-id="element-5068">
                              <img src={`/api/v1/files/${formData[`${field.id}_file_id`]}/thumbnail`} alt="Document preview" className="h-full w-full object-cover" data-id="element-5069" />
                            </div> : <div className="h-16 w-16 rounded bg-blue-50 flex items-center justify-center text-blue-500" data-id="element-5070">
                              <FileTextIcon className="h-5 w-5" data-id="element-5071" />
                            </div>}
                        </div>
                        <div className="ml-4 flex-1" data-id="element-5072">
                          <h4 className="text-sm font-medium text-gray-900" data-id="element-5073">
                            {formData[`${field.id}_filename`]}
                          </h4>
                          <p className="text-xs text-gray-500 mt-1" data-id="element-5074">
                            {formData[`${field.id}_filesize`]} •{' '}
                            {formData[`${field.id}_file_type`]?.split('/')[1]?.toUpperCase()}
                          </p>
                          <div className="mt-2" data-id="element-5075">
                            <button type="button" onClick={() => handleRemoveFile(field.id)} className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-red-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-5076">
                              <svg className="h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-5077">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" data-id="element-5078" />
                              </svg>
                              Remove
                            </button>
                          </div>
                        </div>
                      </div> : fileUploads[field.id]?.uploading ? <div className="p-4" data-id="element-5079">
                        <div className="flex items-center mb-2" data-id="element-5080">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-5081">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-5082"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-5083"></path>
                          </svg>
                          <span className="text-sm font-medium text-gray-700" data-id="element-5084">
                            Uploading {fileUploads[field.id].filename}...
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5 mb-1" data-id="element-5085">
                          <div className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" style={{
                    width: `${fileUploads[field.id].progress}%`
                  }} data-id="element-5086"></div>
                        </div>
                        <p className="text-xs text-gray-500 text-right" data-id="element-5087">
                          {fileUploads[field.id].progress}%
                        </p>
                      </div> : <div className="p-4" data-id="element-5088">
                        <div className="flex justify-center items-center border-2 border-dashed border-gray-300 rounded-md py-6" data-id="element-5089">
                          <div className="space-y-1 text-center" data-id="element-5090">
                            <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true" data-id="element-5091">
                              <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4h-12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" data-id="element-5092" />
                            </svg>
                            <div className="flex text-sm text-gray-600" data-id="element-5093">
                              <label htmlFor={field.id} className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500" data-id="element-5094">
                                <span data-id="element-5095">Upload a file</span>
                                <input id={field.id} name={field.id} type="file" className="sr-only" onChange={e => handleFileChange(e, field.id)} required={field.required && !formData[`${field.id}_file_id`]} data-id="element-5096" />
                              </label>
                              <p className="pl-1" data-id="element-5097">or drag and drop</p>
                            </div>
                            <p className="text-xs text-gray-500" data-id="element-5098">
                              PDF, JPG, PNG up to 10MB
                            </p>
                          </div>
                        </div>
                        {fileUploads[field.id]?.error && <div className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded-md" data-id="element-5099">
                            <div className="flex" data-id="element-5100">
                              <svg className="h-5 w-5 text-red-500 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-5101">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" data-id="element-5102" />
                              </svg>
                              <p data-id="element-5103">{fileUploads[field.id].error}</p>
                            </div>
                          </div>}
                      </div>}
                  </div>
                  <p className="mt-1 text-xs text-gray-500" data-id="element-5104">
                    Upload all required supporting documentation. Files must be
                    in PDF, JPG, or PNG format and must not exceed 10MB.
                  </p>
                </div>)}
            {/* Document requirements */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mt-6" data-id="element-5105">
              <h3 className="text-sm font-medium text-gray-900 mb-2" data-id="element-5106">
                Document Requirements
              </h3>
              <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1" data-id="element-5107">
                <li data-id="element-5108">All documents must be clear and legible</li>
                <li data-id="element-5109">Only PDF, JPG, or PNG formats are accepted</li>
                <li data-id="element-5110">Maximum file size is 10MB per document</li>
                <li data-id="element-5111">
                  Documents in languages other than English or Amharic must be
                  accompanied by certified translations
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center" data-id="element-5112">
          <button type="button" onClick={handlePrevStep} className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors" data-id="element-5113">
            <ArrowLeftIcon className="mr-2 h-4 w-4" data-id="element-5114" />
            Back
          </button>
          <div className="flex space-x-3" data-id="element-5115">
            <button type="button" onClick={handleSaveDraft} disabled={loading} className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded-md shadow-sm hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-75 disabled:cursor-not-allowed transition-colors" data-id="element-5116">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-5117" />
              Save Draft
            </button>
            <button type="button" onClick={handleNextStep} className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors" data-id="element-5118">
              Continue
              <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-5119" />
            </button>
          </div>
        </div>
      </div>;
  };
  const renderReviewStep = () => {
    return <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-5120">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200" data-id="element-5121">
          <h2 className="text-lg font-medium text-gray-900" data-id="element-5122">Review & Submit</h2>
          <p className="text-sm text-gray-500" data-id="element-5123">
            Please review your information before submitting your request
          </p>
        </div>
        <div className="p-6" data-id="element-5124">
          {/* Requester Information */}
          <div className="mb-8" data-id="element-5125">
            <h3 className="text-md font-medium text-gray-900 mb-4" data-id="element-5126">
              {requesterType === 'individual' ? 'Personal Information' : 'Company Information'}
            </h3>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-5127">
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 text-sm" data-id="element-5128">
                {requesterType === 'individual' ? <>
                    <div className="col-span-2 sm:grid sm:grid-cols-3 sm:gap-4" data-id="element-5129">
                      <dt className="font-medium text-gray-500" data-id="element-5130">Full Name</dt>
                      <dd className="mt-1 sm:mt-0 sm:col-span-2 text-gray-900" data-id="element-5131">
                        {formData.fullName}
                      </dd>
                    </div>
                    <div className="sm:grid sm:grid-cols-3 sm:gap-4" data-id="element-5132">
                      <dt className="font-medium text-gray-500" data-id="element-5133">Email</dt>
                      <dd className="mt-1 sm:mt-0 sm:col-span-2 text-gray-900" data-id="element-5134">
                        {formData.email}
                      </dd>
                    </div>
                    <div className="sm:grid sm:grid-cols-3 sm:gap-4" data-id="element-5135">
                      <dt className="font-medium text-gray-500" data-id="element-5136">Phone</dt>
                      <dd className="mt-1 sm:mt-0 sm:col-span-2 text-gray-900" data-id="element-5137">
                        {formData.phone}
                      </dd>
                    </div>
                    <div className="col-span-2 sm:grid sm:grid-cols-3 sm:gap-4" data-id="element-5138">
                      <dt className="font-medium text-gray-500" data-id="element-5139">Address</dt>
                      <dd className="mt-1 sm:mt-0 sm:col-span-2 text-gray-900" data-id="element-5140">
                        {formData.address}
                      </dd>
                    </div>
                  </> : <>
                    <div className="col-span-2 sm:grid sm:grid-cols-3 sm:gap-4" data-id="element-5141">
                      <dt className="font-medium text-gray-500" data-id="element-5142">
                        Company Name
                      </dt>
                      <dd className="mt-1 sm:mt-0 sm:col-span-2 text-gray-900" data-id="element-5143">
                        {formData.companyName}
                      </dd>
                    </div>
                    <div className="sm:grid sm:grid-cols-3 sm:gap-4" data-id="element-5144">
                      <dt className="font-medium text-gray-500" data-id="element-5145">
                        Registration Number
                      </dt>
                      <dd className="mt-1 sm:mt-0 sm:col-span-2 text-gray-900" data-id="element-5146">
                        {formData.registrationNumber}
                      </dd>
                    </div>
                    <div className="sm:grid sm:grid-cols-3 sm:gap-4" data-id="element-5147">
                      <dt className="font-medium text-gray-500" data-id="element-5148">Tax ID</dt>
                      <dd className="mt-1 sm:mt-0 sm:col-span-2 text-gray-900" data-id="element-5149">
                        {formData.taxId}
                      </dd>
                    </div>
                    <div className="sm:grid sm:grid-cols-3 sm:gap-4" data-id="element-5150">
                      <dt className="font-medium text-gray-500" data-id="element-5151">Email</dt>
                      <dd className="mt-1 sm:mt-0 sm:col-span-2 text-gray-900" data-id="element-5152">
                        {formData.companyEmail}
                      </dd>
                    </div>
                    <div className="sm:grid sm:grid-cols-3 sm:gap-4" data-id="element-5153">
                      <dt className="font-medium text-gray-500" data-id="element-5154">Phone</dt>
                      <dd className="mt-1 sm:mt-0 sm:col-span-2 text-gray-900" data-id="element-5155">
                        {formData.companyPhone}
                      </dd>
                    </div>
                    <div className="col-span-2 sm:grid sm:grid-cols-3 sm:gap-4" data-id="element-5156">
                      <dt className="font-medium text-gray-500" data-id="element-5157">Address</dt>
                      <dd className="mt-1 sm:mt-0 sm:col-span-2 text-gray-900" data-id="element-5158">
                        {formData.companyAddress}
                      </dd>
                    </div>
                  </>}
              </dl>
            </div>
          </div>
          {/* Service Details */}
          <div className="mb-8" data-id="element-5159">
            <h3 className="text-md font-medium text-gray-900 mb-4" data-id="element-5160">
              Service Details
            </h3>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-5161">
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 text-sm" data-id="element-5162">
                <div className="col-span-2 sm:grid sm:grid-cols-3 sm:gap-4" data-id="element-5163">
                  <dt className="font-medium text-gray-500" data-id="element-5164">Service</dt>
                  <dd className="mt-1 sm:mt-0 sm:col-span-2 text-gray-900" data-id="element-5165">
                    {serviceName}
                  </dd>
                </div>
                <div className="sm:grid sm:grid-cols-3 sm:gap-4" data-id="element-5166">
                  <dt className="font-medium text-gray-500" data-id="element-5167">Requested Date</dt>
                  <dd className="mt-1 sm:mt-0 sm:col-span-2 text-gray-900" data-id="element-5168">
                    {formData.requestDate}
                  </dd>
                </div>
                <div className="sm:grid sm:grid-cols-3 sm:gap-4" data-id="element-5169">
                  <dt className="font-medium text-gray-500" data-id="element-5170">Document Type</dt>
                  <dd className="mt-1 sm:mt-0 sm:col-span-2 text-gray-900" data-id="element-5171">
                    {formData.documentType}
                  </dd>
                </div>
                {formData.additionalInfo && <div className="col-span-2 sm:grid sm:grid-cols-3 sm:gap-4" data-id="element-5172">
                    <dt className="font-medium text-gray-500" data-id="element-5173">
                      Additional Information
                    </dt>
                    <dd className="mt-1 sm:mt-0 sm:col-span-2 text-gray-900" data-id="element-5174">
                      {formData.additionalInfo}
                    </dd>
                  </div>}
              </dl>
            </div>
          </div>
          {/* Supporting Documents */}
          <div className="mb-8" data-id="element-5175">
            <h3 className="text-md font-medium text-gray-900 mb-4" data-id="element-5176">
              Supporting Documents
            </h3>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-5177">
              {formData[`supportingDocs_file_id`] ? <div className="flex items-center" data-id="element-5178">
                  <div className="flex-shrink-0" data-id="element-5179">
                    {formData[`supportingDocs_file_type`]?.startsWith('image/') ? <div className="h-10 w-10 rounded bg-gray-100 flex items-center justify-center overflow-hidden" data-id="element-5180">
                        <img src={`/api/v1/files/${formData[`supportingDocs_file_id`]}/thumbnail`} alt="Document preview" className="h-full w-full object-cover" data-id="element-5181" />
                      </div> : <div className="h-10 w-10 rounded bg-blue-50 flex items-center justify-center text-blue-500" data-id="element-5182">
                        <FileTextIcon className="h-5 w-5" data-id="element-5183" />
                      </div>}
                  </div>
                  <div className="ml-4" data-id="element-5184">
                    <h4 className="text-sm font-medium text-gray-900" data-id="element-5185">
                      {formData[`supportingDocs_filename`]}
                    </h4>
                    <p className="text-xs text-gray-500" data-id="element-5186">
                      {formData[`supportingDocs_filesize`]} •{' '}
                      {formData[`supportingDocs_file_type`]?.split('/')[1]?.toUpperCase()}
                    </p>
                  </div>
                </div> : <p className="text-sm text-gray-500" data-id="element-5187">
                  No supporting documents uploaded
                </p>}
            </div>
          </div>
          {/* Declaration */}
          <div className="mb-8" data-id="element-5188">
            <h3 className="text-md font-medium text-gray-900 mb-4" data-id="element-5189">
              Declaration
            </h3>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-5190">
              <div className="flex items-start" data-id="element-5191">
                <div className="flex-shrink-0 mt-0.5" data-id="element-5192">
                  <input id="declaration" name="declaration" type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-5193" />
                </div>
                <div className="ml-3 text-sm" data-id="element-5194">
                  <label htmlFor="declaration" className="font-medium text-gray-700" data-id="element-5195">
                    I declare that the information provided is true and accurate
                  </label>
                  <p className="text-gray-500" data-id="element-5196">
                    By submitting this application, I confirm that all
                    information provided is correct to the best of my knowledge.
                    I understand that providing false information may result in
                    the rejection of my application and possible legal
                    consequences.
                  </p>
                </div>
              </div>
            </div>
          </div>
          {/* Privacy Notice */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200" data-id="element-5197">
            <h4 className="text-sm font-medium text-blue-800 mb-2" data-id="element-5198">
              Privacy Notice
            </h4>
            <p className="text-xs text-blue-700" data-id="element-5199">
              Your personal information will be processed in accordance with our
              privacy policy and applicable data protection laws. The
              information you provide will be used solely for the purpose of
              processing your service request and will not be shared with third
              parties except as required by law.
            </p>
          </div>
        </div>
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center" data-id="element-5200">
          <button type="button" onClick={handlePrevStep} className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors" data-id="element-5201">
            <ArrowLeftIcon className="mr-2 h-4 w-4" data-id="element-5202" />
            Back
          </button>
          <div className="flex space-x-3" data-id="element-5203">
            <button type="button" onClick={handleSaveDraft} disabled={loading} className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded-md shadow-sm hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-75 disabled:cursor-not-allowed transition-colors" data-id="element-5204">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-5205" />
              Save Draft
            </button>
            <button type="button" onClick={handleSubmit} disabled={loading} className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors" data-id="element-5206">
              {loading ? <>
                  <LoaderIcon className="animate-spin mr-2 h-4 w-4" data-id="element-5207" />
                  Processing...
                </> : <>
                  <CheckIcon className="mr-2 h-4 w-4" data-id="element-5208" />
                  Submit Request
                </>}
            </button>
          </div>
        </div>
      </div>;
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-5209">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-5210">
        {/* Breadcrumb */}
        <button onClick={() => navigate(-1)} className="flex items-center text-sm text-blue-600 hover:text-blue-800 mb-6" data-id="element-5211">
          <ChevronLeftIcon className="h-4 w-4 mr-1" data-id="element-5212" />
          Back
        </button>
        {/* Service Header */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm mb-6" data-id="element-5213">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center" data-id="element-5214">
            <div data-id="element-5215">
              <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5216">
                {serviceName}
              </h1>
              {selectedProfile && <div className="flex items-center" data-id="element-5217">
                  <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center" data-id="element-5218">
                    <span className="text-blue-600 font-medium" data-id="element-5219">
                      {(selectedProfile.name || citizenName).charAt(0)}
                    </span>
                  </div>
                  <p className="ml-2 text-gray-600" data-id="element-5220">
                    Application for:{' '}
                    <span className="font-medium" data-id="element-5221">
                      {selectedProfile.name || citizenName}
                    </span>
                  </p>
                </div>}
            </div>
            <div className="mt-4 md:mt-0 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-full flex items-center" data-id="element-5222">
              <span className="h-2 w-2 bg-amber-500 rounded-full mr-2" data-id="element-5223"></span>
              <span className="text-sm font-medium text-amber-800" data-id="element-5224">
                Draft Mode
              </span>
            </div>
          </div>
        </div>
        {/* Step Indicator */}
        {renderStepIndicator()}
        {/* Current Step Content */}
        {currentStep === 1 && renderRequesterTypeStep()}
        {currentStep === 2 && renderSearchAndVerificationStep()}
        {currentStep === 3 && renderServiceDetailsStep()}
        {currentStep === 4 && renderDocumentUploadStep()}
        {currentStep === 5 && renderReviewStep()}
      </div>
    </div>;
};